/**
 * Readable UI text helpers (Cinzel display + Karla body).
 * Replaces hard-to-read Kibyra bitmap fonts for shell chrome.
 */

import Phaser from 'phaser';
import { FONT_DISPLAY, FONT_UI, UI, hexColor } from './theme';

export type UiTextTone = 'title' | 'accent' | 'body' | 'muted' | 'brown';

const TONE_COLOR: Record<UiTextTone, string> = {
  title: hexColor(UI.goldBright),
  accent: hexColor(UI.goldBright),
  body: hexColor(UI.textBody),
  muted: hexColor(UI.textMuted),
  brown: hexColor(UI.parchmentDim),
};

export function addUiText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  options: {
    tone?: UiTextTone;
    size?: number;
    display?: boolean;
    originX?: number;
    originY?: number;
    align?: 'left' | 'center' | 'right';
    maxWidth?: number;
    bold?: boolean;
  } = {},
): Phaser.GameObjects.Text {
  const tone = options.tone ?? 'body';
  const size = options.size ?? 14;
  // Cinzel only for larger titles — thin serifs get muddy under ~14px.
  const preferDisplay =
    options.display ?? (tone === 'title' || (tone === 'accent' && size >= 14));
  const t = scene.add
    .text(x, y, text, {
      fontFamily: preferDisplay ? FONT_DISPLAY : FONT_UI,
      fontSize: `${size}px`,
      color: TONE_COLOR[tone],
      fontStyle: options.bold || preferDisplay || tone === 'accent' ? '700' : '600',
      align: options.align ?? 'left',
      wordWrap: options.maxWidth ? { width: options.maxWidth } : undefined,
    })
    .setOrigin(options.originX ?? 0, options.originY ?? 0);
  return t;
}

/** @deprecated Use addUiText — kept so existing call sites compile during migration. */
export function addPixelText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  options: {
    font?: 'ui-yellow' | 'ui-grey' | 'ui-brown';
    size?: number;
    originX?: number;
    originY?: number;
    align?: 'left' | 'center' | 'right';
    maxWidth?: number;
  } = {},
): Phaser.GameObjects.Text {
  const tone: UiTextTone =
    options.font === 'ui-grey'
      ? 'muted'
      : options.font === 'ui-brown'
        ? 'brown'
        : 'accent';
  const size = options.size ?? 14;
  return addUiText(scene, x, y, text, {
    tone,
    size,
    originX: options.originX,
    originY: options.originY,
    align: options.align,
    maxWidth: options.maxWidth,
    // Let addUiText pick Cinzel only for larger accent/title sizes.
  });
}

export function sanitizePixelText(text: string): string {
  return text;
}
